import { $isListNode, ListNode } from '@lexical/list';
import type { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $findMatchingParent, $getNearestNodeOfType } from '@lexical/utils';
import { $getSelection, $isRangeSelection, $isRootOrShadowRoot } from 'lexical';
import { useEffect, useState } from 'react';

type BlockType = 'bullet' | 'h1' | 'h2' | 'h3' | 'number' | 'paragraph';

type ToolbarState = {
    readonly blockType: BlockType;
    readonly isBold: boolean;
    readonly isBulletList: boolean;
    readonly isNumberedList: boolean;
    readonly isUnderline: boolean;
};

export const useToolbarState = (editor: ReturnType<typeof useLexicalComposerContext>[0]): ToolbarState => {
    const [isBold, setIsBold] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [blockType, setBlockType] = useState<BlockType>('paragraph');

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: suppressed due to migration
            editorState.read(() => {
                const selection = $getSelection();

                if ($isRangeSelection(selection)) {
                    setIsBold(selection.hasFormat('bold'));
                    setIsUnderline(selection.hasFormat('underline'));

                    const anchorNode = selection.anchor.getNode();
                    let element =
                        anchorNode.getKey() === 'root'
                            ? anchorNode
                            : $findMatchingParent(anchorNode, (node) => {
                                  const parent = node.getParent();

                                  return parent !== null && $isRootOrShadowRoot(parent);
                              });

                    element ??= anchorNode.getTopLevelElementOrThrow();

                    if ($isListNode(element)) {
                        const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
                        const listType = parentList !== null ? parentList.getListType() : element.getListType();

                        if (listType === 'bullet') {
                            setBlockType('bullet');
                        } else if (listType === 'number') {
                            setBlockType('number');
                        }

                        return;
                    }

                    const type = $isHeadingNode(element) ? element.getTag() : element.getType();

                    if (type === 'h1' || type === 'h2' || type === 'h3') {
                        setBlockType(type);
                    } else {
                        setBlockType('paragraph');
                    }
                }
            });
        });
    }, [editor]);

    return {
        blockType,
        isBold,
        isBulletList: blockType === 'bullet',
        isNumberedList: blockType === 'number',
        isUnderline,
    };
};
